// SwipeableCard.tsx
// A reusable swipeable card component for mobile action buttons
// Usage: Wrap any card content to enable swipe-to-reveal actions

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Box, ActionIcon, Text, useMantineTheme } from "@mantine/core";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export interface SwipeAction {
  /** Unique key for the action */
  key: string;
  /** Icon name from AppIcon */
  icon: string;
  /** Accessible label for the action */
  label: string;
  /** Color for the action button (Mantine color) */
  color: string;
  /** Click handler */
  onClick: () => void;
  /** Whether the action is in progress (shows loading state) */
  loading?: boolean;
  /** Whether the action is disabled */
  disabled?: boolean;
  /**
   * A "secondary" action — comments, and anything that ISN'T edit / remove / archive / duplicate. When a card
   * is given a single `leftSwipeActions` list (the norm), its secondary actions are auto-routed to the LEFT
   * side (revealed by swiping RIGHT), keeping the primary edit/remove/archive/duplicate on the right side.
   */
  secondary?: boolean;
}

export interface SwipeableCardProps {
  /** Card content to display */
  children: React.ReactNode;
  /** Actions to show when swiped left (appear on right side) */
  leftSwipeActions?: SwipeAction[];
  /** Actions to show when swiped right (appear on left side) */
  rightSwipeActions?: SwipeAction[];
  /** Width of each action button in pixels */
  actionWidth?: number;
  /** Swipe threshold as percentage of total action width (0-1) */
  threshold?: number;
  /** Whether swipe is disabled */
  disabled?: boolean;
  /** Called when swipe state changes (open/closed) */
  onSwipeStateChange?: (
    isOpen: boolean,
    direction: "left" | "right" | null
  ) => void;
  /** External control to close the card */
  forceClose?: boolean;
  /** Callback when force close is complete */
  onForceCloseComplete?: () => void;
  /** Whether to show action labels below icons */
  showLabels?: boolean;
  /** Animation duration in ms */
  animationDuration?: number;
  /** Border radius to match parent card */
  borderRadius?: number | string;
}

// ============================================
// Constants
// ============================================

const DEFAULT_ACTION_WIDTH = 82;
const DEFAULT_THRESHOLD = 0.4;
const DEFAULT_ANIMATION_DURATION = 200;
const VELOCITY_THRESHOLD = 0.5; // pixels per ms

// ============================================
// SwipeableCard Component
// ============================================

export const SwipeableCard: React.FC<SwipeableCardProps> = ({
  children,
  leftSwipeActions: leftSwipeActionsProp = [],
  rightSwipeActions: rightSwipeActionsProp = [],
  actionWidth = DEFAULT_ACTION_WIDTH,
  threshold = DEFAULT_THRESHOLD,
  disabled = false,
  onSwipeStateChange,
  forceClose = false,
  onForceCloseComplete,
  showLabels = true,
  animationDuration = DEFAULT_ANIMATION_DURATION,
  borderRadius = 0,
}) => {
  // Two-sided convention: when the caller passes a single list (the norm) and no explicit right-side list,
  // auto-route its `secondary` actions (comments + anything other than edit/remove/archive/duplicate) to the
  // LEFT side (revealed by swiping RIGHT), keeping the primary actions on the right (swipe left). An explicit
  // rightSwipeActions opts out and is used verbatim.
  const leftSwipeActions = rightSwipeActionsProp.length ? leftSwipeActionsProp : leftSwipeActionsProp.filter((a) => !a.secondary);
  const rightSwipeActions = rightSwipeActionsProp.length ? rightSwipeActionsProp : leftSwipeActionsProp.filter((a) => a.secondary);

  const theme = useMantineTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Touch tracking state
  const [translateX, setTranslateX] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [openDirection, setOpenDirection] = useState<"left" | "right" | null>(
    null
  );

  // Touch tracking refs (don't trigger re-renders)
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const currentTranslateX = useRef(0);
  const isDragging = useRef(false);
  const isHorizontalSwipe = useRef<boolean | null>(null);
  const justSwiped = useRef(false); // Guard against click after swipe

  // Calculate max swipe distances
  const leftSwipeMaxDistance = leftSwipeActions.length * actionWidth;
  const rightSwipeMaxDistance = rightSwipeActions.length * actionWidth;

  // Handle force close from parent
  useEffect(() => {
    if (forceClose && translateX !== 0) {
      closeCard();
      onForceCloseComplete?.();
    }
    // ⓘ An EDGE on `forceClose` (#129): `closeCard` is declared below (a dependency array is read during render, where it
    // does not exist yet), `translateX` is read at that moment, and the parent's `onForceCloseComplete` is usually inline.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceClose]);

  // Close card function
  const closeCard = useCallback(() => {
    setIsAnimating(true);
    setTranslateX(0);
    currentTranslateX.current = 0;
    setOpenDirection(null);
    onSwipeStateChange?.(false, null);

    setTimeout(() => {
      setIsAnimating(false);
    }, animationDuration);
  }, [animationDuration, onSwipeStateChange]);

  // Open card to a specific direction
  const openCard = useCallback(
    (direction: "left" | "right") => {
      setIsAnimating(true);
      const targetX =
        direction === "left" ? -leftSwipeMaxDistance : rightSwipeMaxDistance;
      setTranslateX(targetX);
      currentTranslateX.current = targetX;
      setOpenDirection(direction);
      onSwipeStateChange?.(true, direction);

      setTimeout(() => {
        setIsAnimating(false);
      }, animationDuration);
    },
    [
      leftSwipeMaxDistance,
      rightSwipeMaxDistance,
      animationDuration,
      onSwipeStateChange,
    ]
  );

  // ⚠ A PORTAL the card renders (a sheet or a menu its "More" action opens) is OUTSIDE the card in the DOM but INSIDE it
  //   in the React tree, and React runs these handlers for its events too: a tap on the open sheet's row was taken for a
  //   tap on the open card — the card closed and the row never ran (#138 review) — and a drag on the sheet moved the card
  //   behind it. Only what happens inside the card's own DOM is the card's.
  const fromThisCard = useCallback((e: React.SyntheticEvent): boolean => !!contentRef.current?.contains(e.target as Node), []);

  // Touch start handler
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || !fromThisCard(e)) return;

      const touch = e.touches[0];
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      touchStartTime.current = Date.now();
      isDragging.current = true;
      isHorizontalSwipe.current = null;

      // If animating, stop it
      if (isAnimating) {
        setIsAnimating(false);
      }
    },
    [disabled, isAnimating, fromThisCard]
  );

  // Touch move handler
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging.current || disabled) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;

      // Determine swipe direction on first significant move
      if (isHorizontalSwipe.current === null) {
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        if (absX > 10 || absY > 10) {
          isHorizontalSwipe.current = absX > absY;

          if (!isHorizontalSwipe.current) {
            // Vertical swipe - let it scroll
            isDragging.current = false;
            return;
          }
        } else {
          return; // Not enough movement yet
        }
      }

      if (!isHorizontalSwipe.current) return;

      // Prevent vertical scroll while swiping horizontally
      e.preventDefault();

      // Calculate new position
      let newTranslateX = currentTranslateX.current + deltaX;

      // Apply resistance at boundaries
      const hasLeftActions = leftSwipeActions.length > 0;
      const hasRightActions = rightSwipeActions.length > 0;

      // Limit swipe based on available actions
      if (!hasLeftActions && newTranslateX < 0) {
        newTranslateX = newTranslateX * 0.2; // Strong resistance
      } else if (!hasRightActions && newTranslateX > 0) {
        newTranslateX = newTranslateX * 0.2; // Strong resistance
      } else if (newTranslateX < -leftSwipeMaxDistance) {
        // Past left boundary - add resistance
        const overshoot = newTranslateX + leftSwipeMaxDistance;
        newTranslateX = -leftSwipeMaxDistance + overshoot * 0.2;
      } else if (newTranslateX > rightSwipeMaxDistance) {
        // Past right boundary - add resistance
        const overshoot = newTranslateX - rightSwipeMaxDistance;
        newTranslateX = rightSwipeMaxDistance + overshoot * 0.2;
      }

      setTranslateX(newTranslateX);
    },
    [
      disabled,
      leftSwipeActions.length,
      rightSwipeActions.length,
      leftSwipeMaxDistance,
      rightSwipeMaxDistance,
    ]
  );

  // Touch end handler
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging.current || disabled) return;

      isDragging.current = false;

      if (!isHorizontalSwipe.current) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaTime = Date.now() - touchStartTime.current;
      const velocity = Math.abs(deltaX) / deltaTime;

      // Determine final state based on position and velocity
      const currentX = translateX;
      // const wasOpenLeft = openDirection === "left";
      // const wasOpenRight = openDirection === "right";

      let shouldOpenLeft = false;
      let shouldOpenRight = false;

      if (leftSwipeActions.length > 0) {
        // Check for left swipe (reveals right actions)
        if (currentX < 0) {
          const thresholdDistance = leftSwipeMaxDistance * threshold;
          shouldOpenLeft =
            Math.abs(currentX) > thresholdDistance ||
            (velocity > VELOCITY_THRESHOLD && deltaX < 0);
        }
      }

      if (rightSwipeActions.length > 0) {
        // Check for right swipe (reveals left actions)
        if (currentX > 0) {
          const thresholdDistance = rightSwipeMaxDistance * threshold;
          shouldOpenRight =
            currentX > thresholdDistance ||
            (velocity > VELOCITY_THRESHOLD && deltaX > 0);
        }
      }

      // Update state
      currentTranslateX.current = translateX;

      // Mark that we just completed a swipe gesture
      if (isHorizontalSwipe.current) {
        justSwiped.current = true;
        setTimeout(() => {
          justSwiped.current = false;
        }, 100);
      }

      if (shouldOpenLeft) {
        openCard("left");
      } else if (shouldOpenRight) {
        openCard("right");
      } else {
        closeCard();
      }
    },
    [
      disabled,
      translateX,
      leftSwipeActions.length,
      rightSwipeActions.length,
      leftSwipeMaxDistance,
      rightSwipeMaxDistance,
      threshold,
      openCard,
      closeCard,
    ]
  );

  // Handle click on card content - close if open, otherwise let it propagate.
  //
  // Bound in the CAPTURE phase (onClickCapture): a card whose body navigates on
  // tap has its own onClick on a CHILD, which in the bubble phase would already
  // have fired before we got here — so an "open" card would navigate instead of
  // closing (real bug on the Pages list). Capturing lets us swallow the tap first.
  const handleContentClick = useCallback(
    (e: React.MouseEvent) => {
      if (!fromThisCard(e)) {
        return;
      }

      // Ignore click if it's a result of a swipe gesture
      if (justSwiped.current) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }

      // If actions are revealed, close them and prevent the click from propagating
      if (openDirection !== null) {
        e.stopPropagation();
        e.preventDefault();
        closeCard();
      }
      // Otherwise, let the click propagate normally to children
    },
    [openDirection, closeCard, fromThisCard]
  );

  // Handle action click
  const handleActionClick = useCallback(
    (action: SwipeAction, e: React.MouseEvent) => {
      e.stopPropagation();
      if (action.disabled || action.loading) return;

      action.onClick();
      // Optionally close after action
      // closeCard();
    },
    []
  );

  // Render action buttons
  const renderActions = useCallback(
    (actions: SwipeAction[], side: "left" | "right") => {
      if (actions.length === 0) return null;

      return (
        <Box
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            [side]: 0,
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            overflow: "hidden",
            borderRadius:
              side === "left"
                ? `${borderRadius} 0 0 ${borderRadius}`
                : `0 ${borderRadius} ${borderRadius} 0`,
          }}
        >
          {actions.map((action) => (
            <Box
              key={action.key}
              onClick={(e) => handleActionClick(action, e)}
              style={{
                width: actionWidth,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                // Breathing room so the icon + label never crowd the card edge.
                paddingInline: 6,
                paddingBlock: 8,
                backgroundColor:
                  theme.colors[action.color]?.[6] || action.color,
                cursor: action.disabled ? "not-allowed" : "pointer",
                opacity: action.disabled ? 0.5 : 1,
                transition: "opacity 150ms ease",
              }}
            >
              <ActionIcon
                variant="transparent"
                color="white"
                size="lg"
                loading={action.loading}
                disabled={action.disabled}
                aria-label={action.label}
              >
                <AppIcon iconName={action.icon} size={22} color="white" />
              </ActionIcon>
              {showLabels && (
                <Text size="md" c="white" mt={4} fw={500} ta="center" style={{ lineHeight: 1.1 }}>
                  {action.label}
                </Text>
              )}
            </Box>
          ))}
        </Box>
      );
    },
    [actionWidth, borderRadius, handleActionClick, showLabels, theme.colors]
  );

  // While the card is slid to reveal actions, the revealing side is squared off
  // so the card meets the action buttons flush — no rounded-corner gap showing the
  // page behind. CSS radius order: top-left top-right bottom-right bottom-left.
  const radius = useMemo<number | string>(() => {
    const br = borderRadius;

    if (translateX < 0) {
      // Slid left → right actions revealed → square the right corners.
      return `${br} 0 0 ${br}`;
    }

    if (translateX > 0) {
      // Slid right → left actions revealed → square the left corners.
      return `0 ${br} ${br} 0`;
    }

    return br;
  }, [translateX, borderRadius]);

  const contentStyle = useMemo(
    () => ({
      transform: `translateX(${translateX}px)`,
      transition: isAnimating
        ? `transform ${animationDuration}ms ease-out`
        : "none",
      position: "relative" as const,
      zIndex: 1,
      overflow: "hidden" as const,
      backgroundColor: "var(--mantine-color-body)",
      borderRadius: radius,
    }),
    [translateX, isAnimating, animationDuration, radius]
  );

  // The child card (e.g. a Mantine <Paper radius="md">) carries its OWN rounded
  // corners + border; clipping the wrapper alone leaves that rounded border
  // showing on the revealed side. Push the same squared radius onto the child so
  // its border squares off flush with the buttons too.
  const styledChildren = useMemo(() => {
    if (!React.isValidElement(children)) {
      return children;
    }

    const child = children as React.ReactElement<{ style?: React.CSSProperties }>;

    return React.cloneElement(child, {
      style: { ...(child.props.style || {}), borderRadius: radius },
    });
  }, [children, radius]);

  return (
    <Box
      ref={containerRef}
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius,
        touchAction: "pan-y",
      }}
    >
      {/* Left actions (revealed by right swipe) */}
      {renderActions(rightSwipeActions, "left")}

      {/* Right actions (revealed by left swipe) */}
      {renderActions(leftSwipeActions, "right")}

      {/* Main content */}
      <Box
        ref={contentRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClickCapture={handleContentClick}
        style={contentStyle}
      >
        {styledChildren}
      </Box>
    </Box>
  );
};

// ============================================
// Hook for managing swipeable cards in a list
// ============================================

export interface UseSwipeableListOptions {
  /** Callback when any card is opened */
  onCardOpen?: (id: string) => void;
}

export interface UseSwipeableListReturn {
  /** Currently open card ID */
  openCardId: string | null;
  /** Open a specific card */
  openCard: (id: string) => void;
  /** Close all cards */
  closeAllCards: () => void;
  /** Check if a specific card should be force-closed */
  shouldForceClose: (id: string) => boolean;
  /** Handle force close complete for a card */
  handleForceCloseComplete: (id: string) => void;
}

/**
 * Hook to manage swipeable cards in a list
 * Ensures only one card is open at a time
 */
export const useSwipeableList = (
  options: UseSwipeableListOptions = {}
): UseSwipeableListReturn => {
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const [forceCloseId, setForceCloseId] = useState<string | null>(null);

  const openCard = useCallback(
    (id: string) => {
      if (openCardId && openCardId !== id) {
        // Close currently open card first
        setForceCloseId(openCardId);
      }
      setOpenCardId(id);
      options.onCardOpen?.(id);
    },
    [openCardId, options]
  );

  const closeAllCards = useCallback(() => {
    if (openCardId) {
      setForceCloseId(openCardId);
      setOpenCardId(null);
    }
  }, [openCardId]);

  const shouldForceClose = useCallback(
    (id: string) => {
      return forceCloseId === id;
    },
    [forceCloseId]
  );

  const handleForceCloseComplete = useCallback((id: string) => {
    setForceCloseId((current) => (current === id ? null : current));
  }, []);

  return {
    openCardId,
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  };
};

export default SwipeableCard;
